/**
 * Markdown BOM Export Module
 *
 * Generates comprehensive BOM documentation in Markdown format with tables,
 * calculations, detailed derivations, and ASCII topology diagrams.
 * Fully integrated with Phase 2 calculators (platforms, network, power, validation).
 */

import type { BillOfMaterials, BOMSection, BOMLineItem } from '../types/bom';
import type { ClusterConfiguration } from '../types/configuration';

/**
 * Export Options for Markdown
 */
export interface MarkdownExportOptions {
  includeMetadata?: boolean;
  includeDerivations?: boolean;
  includeTOC?: boolean;
  includeTopologyDiagram?: boolean;
  includeCosts?: boolean;
  includeValidationWarnings?: boolean;
  filename?: string;
}

/**
 * Format a number with thousand separators
 */
function formatNumber(num: number): string {
  return num.toLocaleString('en-US');
}

/**
 * Format a floating point number to specified decimal places
 */
function formatFloat(num: number, decimals = 1): string {
  return num.toFixed(decimals);
}

/**
 * Format currency
 */
function formatCurrency(amount: number, currency = 'USD'): string {
  if (currency === 'USD') {
    return `$${formatNumber(Math.round(amount))}`;
  }
  return `${formatNumber(Math.round(amount))} ${currency}`;
}

/**
 * Create a markdown table
 */
function createTable(
  headers: string[],
  rows: string[][],
  alignment?: ('left' | 'right' | 'center')[]
): string {
  const lines: string[] = [];

  // Header row
  lines.push(`| ${headers.join(' | ')} |`);

  // Separator row
  const separators = headers.map((_, i) => {
    const align = alignment?.[i] || 'left';
    if (align === 'center') return ':---:';
    if (align === 'right') return '---:';
    return ':---';
  });
  lines.push(`| ${separators.join(' | ')} |`);

  // Data rows
  rows.forEach((row) => {
    lines.push(`| ${row.join(' | ')} |`);
  });

  return lines.join('\n');
}

/**
 * Generate ASCII art topology diagram for fat-tree network
 */
function generateTopologyDiagram(bom: BillOfMaterials): string {
  const lines: string[] = [];
  const spineCount = bom.summary.network.switchBreakdown.eastWestSpine;
  const leafCount = bom.summary.network.switchBreakdown.eastWestLeaf;
  const nodeCount = bom.summary.compute.totalNodes;

  lines.push('```');
  lines.push('Fat-Tree Topology (2-Tier Leaf-Spine)');
  lines.push('');

  // Spine layer
  lines.push('Spine Layer:');
  const spineSymbols = Array(Math.min(spineCount, 8))
    .fill(0)
    .map((_, i) => `[S${i + 1}]`)
    .join('  ');
  lines.push(`  ${spineSymbols}${spineCount > 8 ? ` ... (${spineCount} total)` : ''}`);
  lines.push('   |    |    |    |');

  // Leaf layer
  lines.push('Leaf Layer:');
  const leafSymbols = Array(Math.min(leafCount, 8))
    .fill(0)
    .map((_, i) => `[L${i + 1}]`)
    .join('  ');
  lines.push(`  ${leafSymbols}${leafCount > 8 ? ` ... (${leafCount} total)` : ''}`);
  lines.push('   |    |    |    |');

  // Compute nodes
  lines.push('Compute Nodes:');
  const nodeSymbols = Array(Math.min(nodeCount, 8))
    .fill(0)
    .map((_, i) => `[C${i + 1}]`)
    .join(' ');
  lines.push(`  ${nodeSymbols}${nodeCount > 8 ? ` ... (${nodeCount} total)` : ''}`);

  lines.push('');
  lines.push('Legend:');
  lines.push('  [Sx] = Spine Switch x');
  lines.push('  [Lx] = Leaf Switch x');
  lines.push('  [Cx] = Compute Node x');
  lines.push('```');

  return lines.join('\n');
}

/**
 * Generate complete BOM in Markdown format
 *
 * @param bom - Complete Bill of Materials
 * @param config - Configuration object
 * @param options - Export options
 * @returns Markdown string
 */
export function generateMarkdownBOM(
  bom: BillOfMaterials,
  config?: ClusterConfiguration,
  options: MarkdownExportOptions = {}
): string {
  const {
    includeMetadata = true,
    includeDerivations = true,
    includeTOC = true,
    includeTopologyDiagram = true,
    includeCosts = true,
    includeValidationWarnings = true,
  } = options;

  const sections: string[] = [];

  // Title
  sections.push(`# ${bom.metadata.title}`);
  sections.push('');

  // Metadata section
  if (includeMetadata) {
    sections.push('## Document Information');
    sections.push('');
    // Handle generatedAt which might be Date or string
    const generatedAtStr = bom.metadata.generatedAt instanceof Date
      ? bom.metadata.generatedAt.toISOString()
      : String(bom.metadata.generatedAt);

    sections.push(
      createTable(
        ['Field', 'Value'],
        [
          ['BOM ID', bom.metadata.bomId],
          ['Configuration ID', bom.metadata.configurationId],
          ['Generated At', generatedAtStr],
          ['Generator Version', bom.metadata.version],
          ...(bom.metadata.description ? [['Description', bom.metadata.description]] : []),
          ...(bom.metadata.generatedBy ? [['Generated By', bom.metadata.generatedBy]] : []),
        ],
        ['left', 'left']
      )
    );
    sections.push('');
  }

  // Table of Contents
  if (includeTOC) {
    sections.push('## Table of Contents');
    sections.push('');
    sections.push('1. [Executive Summary](#executive-summary)');
    sections.push('2. [Compute Resources](#compute-resources)');
    sections.push('3. [Network Infrastructure](#network-infrastructure)');
    sections.push('4. [Power and Cooling](#power-and-cooling)');
    sections.push('5. [Space Requirements](#space-requirements)');
    if (includeCosts && bom.summary.cost) {
      sections.push('6. [Cost Summary](#cost-summary)');
    }
    sections.push(`${includeCosts && bom.summary.cost ? '7' : '6'}. [Detailed Bill of Materials](#detailed-bill-of-materials)`);
    sections.push(`${includeCosts && bom.summary.cost ? '8' : '7'}. [Assumptions and Notes](#assumptions-and-notes)`);
    if (includeValidationWarnings && bom.warnings.length > 0) {
      sections.push(`${includeCosts && bom.summary.cost ? '9' : '8'}. [Validation Warnings](#validation-warnings)`);
    }
    sections.push('');
  }

  // Executive Summary
  sections.push('## Executive Summary');
  sections.push('');
  sections.push(
    createTable(
      ['Metric', 'Value'],
      [
        ['Total GPUs', formatNumber(bom.summary.compute.totalGPUs)],
        ['GPU Model', bom.summary.compute.gpuModel],
        ['Compute Nodes', formatNumber(bom.summary.compute.totalNodes)],
        ['Total Racks', formatNumber(bom.summary.space.totalRacks)],
        ['Total IT Power', `${formatFloat(bom.summary.power.totalITPowerKw)} kW`],
        ['Total Facility Power', `${formatFloat(bom.summary.power.totalFacilityPowerKw)} kW`],
        ['PUE', formatFloat(bom.summary.power.pue, 2)],
        ['Floor Space', `${formatNumber(bom.summary.space.floorSpaceSqFt)} sq ft`],
        [
          'Bisection Bandwidth',
          `${formatFloat(bom.summary.network.bisectionBandwidthTbps, 1)} Tbps`,
        ],
        ...(bom.summary.cost
          ? [['Total Capital Cost', formatCurrency(bom.summary.cost.totalCapex, bom.summary.cost.currency)]]
          : []),
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  // Compute Resources Section
  sections.push('## Compute Resources');
  sections.push('');
  sections.push('### GPU Configuration');
  sections.push('');
  sections.push(
    createTable(
      ['Specification', 'Value'],
      [
        ['GPU Model', bom.summary.compute.gpuModel],
        ['Total GPUs', formatNumber(bom.summary.compute.totalGPUs)],
        ['Total Compute Nodes', formatNumber(bom.summary.compute.totalNodes)],
        ['GPU Memory (Total)', `${formatNumber(bom.summary.compute.totalGPUMemoryGB)} GB`],
        ...(bom.summary.compute.peakTFlops
          ? [['Peak Performance', `${formatFloat(bom.summary.compute.peakTFlops)} TFLOPS`]]
          : []),
        ...(bom.summary.compute.gpuInterconnectBandwidth
          ? [['GPU Interconnect Bandwidth', `${formatFloat(bom.summary.compute.gpuInterconnectBandwidth)} TB/s`]]
          : []),
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  sections.push('### CPU and Memory');
  sections.push('');
  sections.push(
    createTable(
      ['Specification', 'Value'],
      [
        ['Total CPU Cores', formatNumber(bom.summary.compute.totalCPUCores)],
        ['Total System Memory', `${formatNumber(bom.summary.compute.totalMemoryGB)} GB`],
        ['Memory per Node', `${formatFloat(bom.summary.compute.totalMemoryGB / bom.summary.compute.totalNodes)} GB`],
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  // Network Infrastructure Section
  sections.push('## Network Infrastructure');
  sections.push('');
  sections.push('### Switch Breakdown');
  sections.push('');
  sections.push(
    createTable(
      ['Switch Type', 'Count'],
      [
        ['East/West Leaf', formatNumber(bom.summary.network.switchBreakdown.eastWestLeaf)],
        ['East/West Spine', formatNumber(bom.summary.network.switchBreakdown.eastWestSpine)],
        ['North/South Leaf', formatNumber(bom.summary.network.switchBreakdown.northSouthLeaf)],
        ['North/South Spine', formatNumber(bom.summary.network.switchBreakdown.northSouthSpine)],
        ['Management', formatNumber(bom.summary.network.switchBreakdown.management)],
        ['**Total Switches**', `**${formatNumber(bom.summary.network.totalSwitches)}**`],
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  sections.push('### Cable Summary');
  sections.push('');
  const cableRows = Object.entries(bom.summary.network.cableBreakdown).map(([type, count]) => [
    type,
    formatNumber(count),
  ]);
  cableRows.push(['**Total Cables**', `**${formatNumber(bom.summary.network.totalCables)}**`]);
  sections.push(createTable(['Cable Type', 'Count'], cableRows, ['left', 'right']));
  sections.push('');

  sections.push('### Network Performance');
  sections.push('');
  sections.push(
    createTable(
      ['Metric', 'Value'],
      [
        ['Total Bandwidth', `${formatFloat(bom.summary.network.totalBandwidthTbps)} Tbps`],
        ['Bisection Bandwidth', `${formatFloat(bom.summary.network.bisectionBandwidthTbps)} Tbps`],
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  // Topology Diagram
  if (includeTopologyDiagram) {
    sections.push('### Network Topology Diagram');
    sections.push('');
    sections.push(generateTopologyDiagram(bom));
    sections.push('');
  }

  // Power and Cooling Section
  sections.push('## Power and Cooling');
  sections.push('');
  sections.push('### IT Equipment Power');
  sections.push('');
  sections.push(
    createTable(
      ['Component', 'Power (kW)', 'Power (MW)'],
      [
        ['Compute', formatFloat(bom.summary.power.computePowerKw), formatFloat(bom.summary.power.computePowerKw / 1000, 3)],
        ['Network', formatFloat(bom.summary.power.networkPowerKw), formatFloat(bom.summary.power.networkPowerKw / 1000, 3)],
        ['Storage', formatFloat(bom.summary.power.storagePowerKw), formatFloat(bom.summary.power.storagePowerKw / 1000, 3)],
        [
          '**Total IT Power**',
          `**${formatFloat(bom.summary.power.totalITPowerKw)}**`,
          `**${formatFloat(bom.summary.power.totalITPowerKw / 1000, 3)}**`,
        ],
      ],
      ['left', 'right', 'right']
    )
  );
  sections.push('');

  sections.push('### Facility Power');
  sections.push('');
  sections.push(
    createTable(
      ['Component', 'Power (kW)', 'Power (MW)'],
      [
        ['IT Equipment', formatFloat(bom.summary.power.totalITPowerKw), formatFloat(bom.summary.power.totalITPowerKw / 1000, 3)],
        ['Cooling Overhead', formatFloat(bom.summary.power.coolingOverheadKw), formatFloat(bom.summary.power.coolingOverheadKw / 1000, 3)],
        [
          '**Total Facility Power**',
          `**${formatFloat(bom.summary.power.totalFacilityPowerKw)}**`,
          `**${formatFloat(bom.summary.power.totalFacilityPowerKw / 1000, 3)}**`,
        ],
      ],
      ['left', 'right', 'right']
    )
  );
  sections.push('');

  sections.push('### Power Efficiency');
  sections.push('');
  sections.push(
    createTable(
      ['Metric', 'Value'],
      [
        ['PUE (Power Usage Effectiveness)', formatFloat(bom.summary.power.pue, 2)],
        ['Peak Power per Rack', `${formatFloat(bom.summary.power.peakPowerPerRackKw)} kW`],
        ['Average Power per Rack', `${formatFloat(bom.summary.power.avgPowerPerRackKw)} kW`],
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  // Space Requirements Section
  sections.push('## Space Requirements');
  sections.push('');
  sections.push('### Rack Breakdown');
  sections.push('');
  sections.push(
    createTable(
      ['Rack Type', 'Count'],
      [
        ['Compute Racks', formatNumber(bom.summary.space.computeRacks)],
        ['Network Racks', formatNumber(bom.summary.space.networkRacks)],
        ['Storage Racks', formatNumber(bom.summary.space.storageRacks)],
        ['**Total Racks**', `**${formatNumber(bom.summary.space.totalRacks)}**`],
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  sections.push('### Space Utilization');
  sections.push('');
  sections.push(
    createTable(
      ['Metric', 'Value'],
      [
        ['Total Rack Units Used', formatNumber(bom.summary.space.totalRackUnitsUsed)],
        ['Rack Unit Utilization', `${formatFloat(bom.summary.space.rackUtilization * 100)}%`],
        ['Floor Space (sq ft)', formatNumber(bom.summary.space.floorSpaceSqFt)],
        ['Floor Space (sq m)', formatNumber(bom.summary.space.floorSpaceSqM)],
      ],
      ['left', 'right']
    )
  );
  sections.push('');

  // Cost Summary Section
  if (includeCosts && bom.summary.cost) {
    sections.push('## Cost Summary');
    sections.push('');
    sections.push('### Capital Expenditure (CapEx)');
    sections.push('');
    sections.push(
      createTable(
        ['Category', 'Cost'],
        [
          ['Compute', formatCurrency(bom.summary.cost.hardware.compute, bom.summary.cost.currency)],
          ['Network', formatCurrency(bom.summary.cost.hardware.network, bom.summary.cost.currency)],
          ['Storage', formatCurrency(bom.summary.cost.hardware.storage, bom.summary.cost.currency)],
          ['Infrastructure', formatCurrency(bom.summary.cost.hardware.infrastructure, bom.summary.cost.currency)],
          [
            '**Total Hardware**',
            `**${formatCurrency(bom.summary.cost.hardware.total, bom.summary.cost.currency)}**`,
          ],
        ],
        ['left', 'right']
      )
    );
    sections.push('');

    sections.push('### Software & Services');
    sections.push('');
    sections.push(
      createTable(
        ['Category', 'Cost'],
        [
          ['Licenses', formatCurrency(bom.summary.cost.software.licenses, bom.summary.cost.currency)],
          ['Subscriptions', formatCurrency(bom.summary.cost.software.subscriptions, bom.summary.cost.currency)],
          ['Installation', formatCurrency(bom.summary.cost.services.installation, bom.summary.cost.currency)],
          ['Support', formatCurrency(bom.summary.cost.services.support, bom.summary.cost.currency)],
          ['Training', formatCurrency(bom.summary.cost.services.training, bom.summary.cost.currency)],
        ],
        ['left', 'right']
      )
    );
    sections.push('');

    sections.push('### Total Investment');
    sections.push('');
    sections.push(
      createTable(
        ['Component', 'Cost'],
        [
          ['Hardware', formatCurrency(bom.summary.cost.hardware.total, bom.summary.cost.currency)],
          ['Software', formatCurrency(bom.summary.cost.software.total, bom.summary.cost.currency)],
          ['Services', formatCurrency(bom.summary.cost.services.total, bom.summary.cost.currency)],
          [
            '**Total CapEx**',
            `**${formatCurrency(bom.summary.cost.totalCapex, bom.summary.cost.currency)}**`,
          ],
        ],
        ['left', 'right']
      )
    );
    sections.push('');

    // Annual OpEx if available
    if (bom.summary.cost.annualOpex) {
      sections.push('### Annual Operating Expenses (OpEx)');
      sections.push('');
      sections.push(
        createTable(
          ['Category', 'Annual Cost'],
          [
            ['Power', formatCurrency(bom.summary.cost.annualOpex.power, bom.summary.cost.currency)],
            ['Cooling', formatCurrency(bom.summary.cost.annualOpex.cooling, bom.summary.cost.currency)],
            ['Maintenance', formatCurrency(bom.summary.cost.annualOpex.maintenance, bom.summary.cost.currency)],
            ['Support', formatCurrency(bom.summary.cost.annualOpex.support, bom.summary.cost.currency)],
            [
              '**Total Annual OpEx**',
              `**${formatCurrency(bom.summary.cost.annualOpex.total, bom.summary.cost.currency)}**`,
            ],
          ],
          ['left', 'right']
        )
      );
      sections.push('');
    }
  }

  // Detailed Bill of Materials
  sections.push('## Detailed Bill of Materials');
  sections.push('');

  bom.sections.forEach((section) => {
    sections.push(`### ${section.title}`);
    sections.push('');
    if (section.description) {
      sections.push(section.description);
      sections.push('');
    }

    // Build table headers
    const headers = ['Line', 'Component', 'Qty', 'Unit'];
    if (includeDerivations) {
      headers.push('Derivation');
    }
    if (includeCosts) {
      headers.push('Unit Cost', 'Ext. Cost');
    }

    // Build table rows
    const rows: string[][] = [];
    section.items.forEach((item) => {
      const indent = '  '.repeat(item.indentLevel || 0);
      const row = [
        item.lineNumber.toString(),
        `${indent}${item.name}`,
        formatNumber(item.quantity),
        item.unit,
      ];

      if (includeDerivations) {
        row.push(item.derivation);
      }

      if (includeCosts && item.cost) {
        row.push(formatCurrency(item.cost.unitCost, item.cost.currency));
        row.push(formatCurrency(item.cost.extendedCost, item.cost.currency));
      } else if (includeCosts) {
        row.push('—');
        row.push('—');
      }

      rows.push(row);
    });

    // Create alignment
    const alignment: ('left' | 'right' | 'center')[] = ['right', 'left', 'right', 'left'];
    if (includeDerivations) {
      alignment.push('left');
    }
    if (includeCosts) {
      alignment.push('right', 'right');
    }

    sections.push(createTable(headers, rows, alignment));
    sections.push('');

    // Section totals
    if (section.totals.power || section.totals.rackUnits || section.totals.cost) {
      sections.push('**Section Totals:**');
      sections.push('');
      if (section.totals.power) {
        sections.push(`- Power: ${formatFloat(section.totals.power)} kW`);
      }
      if (section.totals.rackUnits) {
        sections.push(`- Rack Units: ${formatNumber(section.totals.rackUnits)} U`);
      }
      if (section.totals.cost && includeCosts) {
        sections.push(`- Cost: ${formatCurrency(section.totals.cost)}`);
      }
      sections.push('');
    }
  });

  // Assumptions and Notes
  sections.push('## Assumptions and Notes');
  sections.push('');
  if (bom.assumptions.length > 0) {
    bom.assumptions.forEach((assumption) => {
      sections.push(`- ${assumption}`);
    });
  } else {
    sections.push('- No assumptions documented');
  }
  sections.push('');

  // Validation Warnings
  if (includeValidationWarnings && bom.warnings.length > 0) {
    sections.push('## Validation Warnings');
    sections.push('');
    sections.push('> **Important:** The following warnings were generated during BOM calculation:');
    sections.push('');
    bom.warnings.forEach((warning) => {
      sections.push(`- ${warning}`);
    });
    sections.push('');
  }

  // Footer
  sections.push('---');
  sections.push('');
  sections.push('*Generated by GPU Cluster BOM Calculator*');
  sections.push(`*BOM Version: ${bom.metadata.version}*`);
  sections.push('');

  return sections.join('\n');
}

/**
 * Download Markdown BOM to file
 *
 * @param bom - Complete Bill of Materials
 * @param config - Configuration object
 * @param filename - Output filename
 * @param options - Export options
 */
export function downloadMarkdownBOM(
  bom: BillOfMaterials,
  config?: ClusterConfiguration,
  filename?: string,
  options: MarkdownExportOptions = {}
): void {
  const markdown = generateMarkdownBOM(bom, config, options);
  const finalFilename = filename || options.filename || `gpu-cluster-bom-${bom.metadata.bomId}.md`;
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', finalFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
